import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan, faPlus, faTachometer, faImage, faArrowLeft, faCheckCircle, faEye, faPencil, faGears, faArrowUpRightFromSquare, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useRecoilState } from 'recoil';

import { CMS_VIDEO_STATE_OPTIONS } from "../../Constants/FieldOptions";
import Select from 'react-select'
import { getCMSImageDetailAPI, deleteCMSImageAPI } from "../../API/CMSImage";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormInputField from "../Reusable/FormInputField";
import FormTextareaField from "../Reusable/FormTextareaField";
import FormMultiSelectField from "../Reusable/FormMultiSelectField";
import FormRadioField from "../Reusable/FormRadioField";
import FormSelectField from "../Reusable/FormSelectField";
import { topAlertMessageState, topAlertStatusState } from "../../AppState";
import PageLoadingContent from "../Reusable/PageLoadingContent";


function ImageDetail() {
    ////
    //// URL Parameters.
    ////

    const { id } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [isFetching, setFetching] = useState(false);
    const [forceURL, setForceURL] = useState("");
    const [image, setImage] = useState(null);
    const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] = useState(false);

    ////
    //// Event handling.
    ////

    const fetchDetail = (e) => {
        setFetching(true);
        setErrors({});
        getCMSImageDetailAPI(
            id,
            onImageDetailSuccess,
            onImageDetailError,
            onImageDetailDone
        );
    }

    const onDeleteConfirmButtonClick = () => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        setFetching(true);
        setErrors({});

        deleteCMSImageAPI(
            id,
            onImageDeleteSuccess,
            onImageDeleteError,
            onImageDeleteDone
        );
        setShowDeletionConfirmationModal(false);
    }

    ////
    //// API.
    ////

    // --- Detail --- //

    function onImageDetailSuccess(response){
        console.log("onImageDetailSuccess: Starting...");
        setImage(response);
    }

    function onImageDetailError(apiErr) {
        console.log("onImageDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onImageDetailDone() {
        console.log("onImageDetailDone: Starting...");
        setFetching(false);
    }

    // --- Delete --- //

    function onImageDeleteSuccess(response){
        console.log("onImageDeleteSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("Image deleted");
        setTimeout(() => {
            console.log("onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Redirect the user to a new page.
        setForceURL("/cms-images");
    }

    function onImageDeleteError(apiErr) {
        console.log("onImageDeleteError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onImageDeleteError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onImageDeleteDone() {
        console.log("onImageDeleteDone: Starting...");
        setFetching(false);
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            fetchDetail();
        }

        return () => { mounted = false; }
    }, [id]);

    // SPECIAL THANKS TO: https://stackoverflow.com/a/9851769
    // Safari 3.0+ "[object HTMLReusableConstructor]"
    let isSafari = /constructor/i.test(window.HTMLReusable) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));

    ////
    //// Component rendering.
    ////

    if (forceURL !== "") {
        return <Navigate to={forceURL}  />
    }

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/cms-images" aria-current="page"><FontAwesomeIcon className="fas" icon={faImage} />&nbsp;Images</Link></li>
                            <li class="is-active"><Link aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Details</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/cms-images`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Images</Link></li>
                        </ul>
                    </nav>

                    {/* Modal(s) */}
                    <nav class="">
                        <div class={`modal ${showDeletionConfirmationModal ? 'is-active' : ''}`}>
                            <div class="modal-background"></div>
                            <div class="modal-card">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">Are you sure?</p>
                                    <button class="delete" aria-label="close" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}></button>
                                </header>
                                <section class="modal-card-body">
                                    You are about to delete this image and all the data inside of it. This action is cannot be undone. Are you sure you would like to continue?
                                </section>
                                <footer class="modal-card-foot">
                                    <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                    <button class="button" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}>Cancel</button>
                                </footer>
                            </div>
                        </div>
                    </nav>

                    {/* Page */}
                    <nav class="box">
                        <div class="columns">
                            <div class="column">
                                <h1 class="title is-4"><FontAwesomeIcon className="fas" icon={faImage} />&nbsp;Image</h1>
                            </div>
                            {!isFetching && <div class="column has-text-right">
                                <button class="button is-small is-info is-fullwidth-mobile" onClick={fetchDetail}>
                                    <FontAwesomeIcon className="mdi" icon={faRefresh} /><span class="is-hidden-desktop is-hidden-tablet">&nbsp;Refresh</span>
                                </button>
                                &nbsp;
                                <Link to={`/cms-image/${id}/edit`}  class="button is-small is-warning is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit Image
                                </Link>
                                &nbsp;
                                <button onClick={(e, ses) => setShowDeletionConfirmationModal(true)} class="button is-small is-danger is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete Image
                                </button>
                            </div>}
                        </div>

                        {isFetching
                            ? <>
                                <PageLoadingContent displayMessage={"Please wait"} />
                            </>
                            :
                            <>
                                <FormErrorBox errors={errors} />
                                {image && <>
                                    {image.state === 1 && <article class="message is-info">
                                        <div class="message-body">
                                            <FontAwesomeIcon className="fas" icon={faGears} />&nbsp;Image is currently queued to be processed.
                                        </div>
                                    </article>}
                                    {image.state === 2 && <article class="message is-info">
                                        <div class="message-body">
                                            <FontAwesomeIcon className="fas" icon={faGears} />&nbsp;Image is currently being processed.
                                        </div>
                                    </article>}
                                </>}

                                <p class="subtitle is-6">Details</p>

                                {!isFetching && image && image.publicObjectUrl && <div class="field">
                                    <label class="label">Image Content</label>
                                    <figure class="image">
                                        <img src={image.publicObjectUrl} />
                                    </figure>
                                </div>}

                                {!isFetching && image && <div class="container">
                                    <FormInputField
                                        label="Name"
                                        name="name"
                                        placeholder="Text input"
                                        value={image.name}
                                        helpText=""
                                        isRequired={true}
                                        maxWidth="150px"
                                        disabled={true}
                                    />

                                    <FormInputField
                                        label="Description"
                                        name="description"
                                        type="text"
                                        placeholder="Text input"
                                        value={image.description}
                                        helpText=""
                                        isRequired={true}
                                        maxWidth="485px"
                                        disabled={true}
                                    />

                                    <FormSelectField
                                        label="State"
                                        name="state"
                                        placeholder="Pick"
                                        selectedValue={image.state}
                                        errorText={errors && errors.state}
                                        helpText=""
                                        options={CMS_VIDEO_STATE_OPTIONS}
                                        disabled={true}
                                    />

                                    <br />
                                    <br />

                                    <div class="columns">
                                        <div class="column is-half">
                                            <Link to={`/cms-images`} class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Images</Link>
                                        </div>
                                        <div class="column is-half has-text-right">
                                            <Link to={`/cms-image/${image.id}/edit`} class="button is-warning is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link>
                                        </div>
                                    </div>

                                </div>}
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default ImageDetail;
